import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';

import { PasswordStrength, TermsFooter, TwoStepHeader } from './components/TwoStepParts';
import { setEmailSuggestionInStore } from './data/actions';
import { FORM_SUBMISSION_ERROR } from './data/constants';
import { validateProfileFields } from './data/profileRules';
import { ACCOUNT_STEP_FIELDS, PROFILE_STEP_ROWS, TWO_STEP_ACCOUNT_FIELDS } from './data/twoStepConstants';
import { isFormValid } from './data/utils';
import twoStepMessages from './twoStepMessages';

const HiddenField = () => null;

/**
 * Two-step registration (per-tenant, MFE_CONFIG `TWO_STEP_REGISTRATION`): account details first, then a mandatory
 * profile step. Everything RegistrationPage needs for it lives here; for other tenants every returned value is
 * inert (empty props, null nodes, fields untouched).
 */
const useTwoStepRegistration = ({
  fieldDescriptions,
  optionalFields,
  formFields,
  configurableFormFields,
  errors,
  temporaryErrors,
  setErrors,
  setErrorCode,
  backendValidations,
  registrationEmbedded,
  currentProvider,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const enabled = !!getConfig().TWO_STEP_REGISTRATION;
  const autoGeneratedUsername = getConfig().ENABLE_AUTO_GENERATED_USERNAME;
  const [step, setStep] = useState(1);
  const onAccountStep = enabled && step === 1;
  const profileFieldNames = PROFILE_STEP_ROWS.flat();

  useEffect(() => {
    // Step 1's fields are unmounted on step 2: send the user back so the field error is visible.
    if (enabled && step === 2 && backendValidations
      && TWO_STEP_ACCOUNT_FIELDS.some(name => backendValidations[name])) {
      setStep(1);
    }
  }, [backendValidations]); // eslint-disable-line react-hooks/exhaustive-deps

  const goToProfileStep = () => {
    // Validate only the account fields; the profile fields are validated on final submit.
    const accountDescriptions = Object.fromEntries(
      Object.entries(fieldDescriptions).filter(([name]) => ACCOUNT_STEP_FIELDS.includes(name)),
    );
    const payload = { ...formFields };
    if (currentProvider) {
      delete payload.password;
    }
    if (autoGeneratedUsername) {
      delete payload.username;
    }
    const { isValid, fieldErrors, emailSuggestion } = isFormValid(
      payload,
      registrationEmbedded ? temporaryErrors : errors,
      configurableFormFields,
      accountDescriptions,
      formatMessage,
    );
    setErrors({ ...fieldErrors });
    dispatch(setEmailSuggestionInStore(emailSuggestion));
    if (isValid) {
      setStep(2);
    } else {
      setErrorCode(prevState => ({ type: FORM_SUBMISSION_ERROR, count: prevState.count + 1 }));
    }
  };

  const fieldProps = (field) => (enabled ? {
    labelAbove: true,
    placeholder: formatMessage(twoStepMessages[`register.step.${field}.placeholder`]),
    floatingLabel: formatMessage(twoStepMessages[`register.step.${field}.label`]),
    helpText: [],
  } : {});

  let submitLabel = null;
  if (onAccountStep) {
    submitLabel = formatMessage(twoStepMessages['register.step.continue.button']);
  } else if (enabled) {
    submitLabel = formatMessage(twoStepMessages['register.step.create.account.button']);
  }

  return {
    enabled,
    // Step 1 fields are not rendered on step 2.
    accountFields: (fields) => (enabled && step === 2 ? fields.map(() => HiddenField) : fields),
    fieldProps,
    header: enabled && <TwoStepHeader step={step} onBack={() => setStep(1)} />,
    usernameHint: onAccountStep && !autoGeneratedUsername && (
      <p className="two-step-reg__hint">{formatMessage(twoStepMessages['register.step.username.hint'])}</p>
    ),
    passwordStrength: onAccountStep && !currentProvider && <PasswordStrength value={formFields.password} />,
    // Props for the configurable-fields component (TwoStepProfileFields replaces ConfigurableRegistrationForm).
    formProps: enabled ? { fieldDescriptions: { ...fieldDescriptions, ...optionalFields.fields }, step } : {},
    submitLabel,
    footer: enabled && step === 2 && <TermsFooter />,
    // Enter/click on step 1 moves on to the profile step instead of registering.
    submit: (registerUser) => (onAccountStep ? goToProfileStep() : registerUser()),
    // Profile fields: same format/length/choice rules as the backend; required ones are checked by isFormValid.
    validateProfile: (fieldErrors, isValid) => {
      if (!enabled || step !== 2) {
        return isValid;
      }
      const profileErrors = validateProfileFields(
        configurableFormFields, { ...fieldDescriptions, ...optionalFields.fields }, formatMessage,
      );
      Object.keys(profileErrors).forEach(name => {
        if (!fieldErrors[name]) { fieldErrors[name] = profileErrors[name]; } // eslint-disable-line no-param-reassign
      });
      const firstInvalid = profileFieldNames.find(name => fieldErrors[name]);
      if (firstInvalid) {
        document.querySelector(`[name="${firstInvalid}"]`)?.focus();
      }
      return !firstInvalid && isValid;
    },
  };
};

export default useTwoStepRegistration;
